import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { InfoIcon, RotateCcw, Trash2Icon } from 'lucide-react';

import { ScrollableCellWithAvatar } from '@/components/data-table/cells';
import { Button } from '@/components/ui/button';
import i18n from '@/core/i18n';
import { formatAmountWithCurrencyCode } from '@/helpers/finance';
import { StatusBadge, StatusBadgeType } from '@/modules/common';

import { BillListItem, BillStatus } from '../types';
import { FORMAT_DATE_SHORT_MONTH_YEAR } from './date-format';

const columnHelper = createColumnHelper<BillListItem>();

export const getBillingTableColumns = ({
  onDeleteClick,
  onErrorInfo,
  onInProgressInfo,
  onRetry,
  isRetrying,
  onErrorBillSelect,
}: {
  onDeleteClick?: (bill: BillListItem) => void;
  onErrorInfo?: (bill: BillListItem) => void;
  onInProgressInfo: (bill: BillListItem) => void;
  onRetry?: (billId: number) => void;
  isRetrying?: (billId: number) => boolean;
  onErrorBillSelect?: (bill: BillListItem) => void;
}) => [
  columnHelper.accessor('number', {
    header: i18n.t('authenticated.bills:headers.billNumber'),
    enableHiding: false,
    cell: ({ getValue, row }) => {
      const { externalId, status } = row.original;
      const noValue = i18n.t('common:table.noValue');

      if (status === BillStatus.InProgress && !externalId)
        return (
          <span className="text-muted-foreground">
            {i18n.t('authenticated.bills:common.billInProgress')}
          </span>
        );

      if (status === BillStatus.Error)
        return <span className="text-muted-foreground">{noValue}</span>;

      return (
        <div style={{ minWidth: '10rem' }} className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span>{getValue() ? `#${getValue()}` : noValue}</span>
          </div>
        </div>
      );
    },
  }),
  columnHelper.accessor('member.name', {
    header: i18n.t('authenticated.bills:headers.memberName'),
    enableHiding: false,
    minSize: 200,
    size: 200,
    maxSize: 400,
    cell: ({ getValue, row }) => (
      <ScrollableCellWithAvatar
        text={getValue() || i18n.t('common:table.noValue')}
        image={row.original.member?.profileImageUrl}
        avatarName={getValue() || ''}
        alt={`${getValue()} profile image`}
      />
    ),
  }),
  columnHelper.accessor('status', {
    header: () => (
      <div className="flex w-full justify-center">
        {i18n.t('authenticated.bills:headers.status')}
      </div>
    ),
    enableHiding: false,
    minSize: 100,
    size: 150,
    maxSize: 240,
    meta: { cellClassName: 'text-center' },
    cell: ({ getValue }) => {
      const status = getValue() || BillStatus.Unknown;

      return (
        <StatusBadge
          type={StatusBadgeType.Bill}
          status={status}
          label={i18n.t(`authenticated.bills:status.${status}`)}
        />
      );
    },
  }),
  columnHelper.accessor('createdOn', {
    header: i18n.t('authenticated.bills:headers.startDate'),
    cell: ({ getValue }) => {
      const dateString = getValue();
      if (!dateString) return i18n.t('common:table.noValue');
      try {
        const date = new Date(dateString);
        return (
          <span className="whitespace-nowrap">{format(date, FORMAT_DATE_SHORT_MONTH_YEAR)}</span>
        );
      } catch {
        return dateString;
      }
    },
  }),
  columnHelper.accessor('period', {
    header: i18n.t('authenticated.bills:headers.period'),
    size: 200,
    cell: ({ getValue }) => (
      <div className="flex min-w-[200px] items-center">
        <span className="whitespace-nowrap">{getValue()}</span>
      </div>
    ),
  }),
  columnHelper.accessor('dueDate', {
    header: i18n.t('authenticated.bills:headers.endDate'),
    cell: ({ getValue }) => {
      const dateString = getValue();
      if (!dateString) return i18n.t('common:table.noValue');
      try {
        const date = new Date(dateString);
        return (
          <span className="whitespace-nowrap">{format(date, FORMAT_DATE_SHORT_MONTH_YEAR)}</span>
        );
      } catch {
        return dateString;
      }
    },
  }),
  columnHelper.accessor('amount', {
    header: i18n.t('authenticated.bills:headers.amount'),
    enableHiding: false,
    cell: ({ getValue, row }) => (
      <div style={{ minWidth: '8rem' }} className="flex items-center">
        <span>{formatAmountWithCurrencyCode(getValue(), row.original.currencyCode)}</span>
      </div>
    ),
  }),
  columnHelper.accessor('balance', {
    header: i18n.t('authenticated.bills:headers.balance'),
    cell: ({ getValue, row }) => (
      <div style={{ minWidth: '8rem' }} className="flex items-center">
        <span>{formatAmountWithCurrencyCode(getValue(), row.original.currencyCode)}</span>
      </div>
    ),
  }),
  columnHelper.display({
    id: 'action',
    header: () => (
      <div className="flex w-full justify-center">
        {i18n.t('authenticated.bills:headers.action')}
      </div>
    ),
    meta: { cellClassName: 'text-center' },
    cell: ({ row }) => {
      const { status } = row.original;
      if (status === BillStatus.InProgress) {
        return (
          <div className="flex items-center justify-center">
            <Button
              size={'icon'}
              variant={'ghost'}
              onClick={e => {
                e.stopPropagation();
                onInProgressInfo(row.original);
              }}
            >
              <InfoIcon size={16} />
            </Button>
          </div>
        );
      }

      if (status === BillStatus.Error) {
        return (
          <div className="flex items-center justify-center gap-2">
            <Button
              size={'icon'}
              variant={'ghost'}
              onClick={e => {
                e.stopPropagation();
                onErrorBillSelect?.(row.original);
                onErrorInfo?.(row.original);
              }}
            >
              <InfoIcon size={16} />
            </Button>
            {onRetry && (
              <Button
                size={'icon'}
                variant={'ghost'}
                onClick={e => {
                  e.stopPropagation();
                  onRetry(row.original.id);
                }}
                disabled={isRetrying?.(row.original.id)}
                title={i18n.t('authenticated.bills:table.retryBill')}
              >
                <RotateCcw size={16} />
              </Button>
            )}
          </div>
        );
      }
      return (
        <div className="flex items-center justify-center">
          {onDeleteClick && (
            <Button
              size={'icon'}
              variant={'ghost'}
              onClick={e => {
                e.stopPropagation();
                onDeleteClick(row.original);
              }}
            >
              <Trash2Icon size={16} />
            </Button>
          )}
        </div>
      );
    },
    size: 50,
  }),
];
